import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Factory as PlantIcon,
  ElectricBolt as ElectricIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  TrendingUp as TrendingIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
  ViewModule as PortfolioIcon,
  AccountBalance as PerformanceIcon,
  MoneyOff as MissedIcon,
  Summarize as SummaryIcon,
  Schedule as AuctionIcon,
  Analytics as AuctionAnalyticsIcon,
  SwapHoriz as CrossBorderIcon,
  AccessTime as IntradayIcon,
  ShowChart as IntradayAnalyticsIcon,
  MenuBook as MethodologyIcon,
  FolderSpecial as FolderIcon,
  AttachMoney as PricesIcon,
  Cable as InterconnectorIcon,
  CloudUpload as UploadIcon,
  Public as EuropeIcon,
  CompareArrows as CompareIcon,
  Hub as PlatformIcon,
  Memory as StrategyIcon,
  CurrencyExchange as AlgoIcon,
  AccountTree as GridSubmissionIcon,
  Bolt as DispatchIcon,
  ShowChart as OrderbookIcon,
  Timeline as TraceIcon,
  Article as EventLogIcon,
  HealthAndSafety as HealthIcon,
  Storage as PositionsDbIcon,
  TuneRounded as OrchestratorIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useApp, ScopeTypes } from '../../context/AppContext';

const DRAWER_WIDTH = 260;

// View modes — sidebar toggle picks one. View choice persists in localStorage.
const VIEW_PLATFORM = 'platform';
const VIEW_LEGACY = 'legacy';
const VIEW_STORAGE_KEY = 'energycosmos_sidebar_view';
// Whether the Platform / Market Data toggle (and the legacy menu) is shown
// at all. Defaults OFF — Market Data pages are mostly legacy and can be
// re-enabled via Platform → Settings.
const SHOW_MARKET_DATA_KEY = 'energycosmos_show_market_data';
const SHOW_ADMIN_KEY = 'energycosmos_show_admin_sections';

function readShowMarketData() {
  try { return localStorage.getItem(SHOW_MARKET_DATA_KEY) === 'true'; } catch { return false; }
}

function readShowAdmin() {
  try { return localStorage.getItem(SHOW_ADMIN_KEY) === 'true'; } catch { return false; }
}

// Platform view: nested by audience.
//
// - Trading: the trader's daily view. Expanded by default.
// - Operations: orchestrator controls + admin actions. Collapsed by default.
// - Observability: trace / event log / adapter health. Admin/dev only,
//   collapsed by default. Adapter health is also surfaced in the top-nav
//   strip on every page so users don't need to dig in here.
// - Admin: settings + reference docs. Collapsed by default.
//
// `audience: 'admin'` items only render when the user has flipped the
// "Show admin sections" toggle in Settings. Default is OFF so the trader
// sees only Trading.
const platformMenu = [
  {
    title: 'Trading',
    audience: 'trader',
    defaultOpen: true,
    items: [
      { text: 'Overview',        icon: <PlatformIcon />,        path: '/platform' },
      { text: 'Market Data',     icon: <OrderbookIcon />,       path: '/platform/marketdata' },
      { text: 'Strategy',        icon: <StrategyIcon />,        path: '/platform/strategy' },
      { text: 'Algo Trading',    icon: <AlgoIcon />,            path: '/platform/algo' },
      { text: 'Grid Submission', icon: <GridSubmissionIcon />,  path: '/platform/grid' },
      { text: 'Dispatch',        icon: <DispatchIcon />,        path: '/platform/dispatch' },
    ],
  },
  {
    title: 'Operations',
    audience: 'admin',
    defaultOpen: false,
    items: [
      { text: 'Orchestrator', icon: <OrchestratorIcon />, path: '/platform/orchestrator' },
    ],
  },
  {
    title: 'Observability',
    audience: 'admin',
    defaultOpen: false,
    items: [
      { text: 'End-to-End Trace',  icon: <TraceIcon />,       path: '/observability/trace' },
      { text: 'Event Log',         icon: <EventLogIcon />,    path: '/observability/events' },
      { text: 'Adapter Health',    icon: <HealthIcon />,      path: '/observability/health' },
      { text: 'PositionService DB', icon: <PositionsDbIcon />, path: '/observability/positions-db' },
    ],
  },
  {
    title: 'Admin',
    audience: 'admin',
    defaultOpen: false,
    items: [
      { text: 'Settings',         icon: <SettingsIcon />, path: '/platform/settings' },
      { text: 'Help & Reference', icon: <HelpIcon />,     path: '/platform/help' },
    ],
  },
];

// Legacy (original EnergyCosmos) market-data dashboard sections.
const legacyMenu = [
  {
    title: 'Overview',
    items: [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
      { text: 'System Status', icon: <SpeedIcon />, path: '/system' },
      { text: 'European Markets', icon: <EuropeIcon />, path: '/market/overview' },
    ],
  },
  {
    title: 'Generation',
    items: [
      { text: 'By Fuel Type', icon: <ElectricIcon />, path: '/generation/fuel' },
      { text: 'Plant Output', icon: <PlantIcon />, path: '/generation/plants' },
      { text: 'Interconnectors', icon: <InterconnectorIcon />, path: '/generation/interconnectors' },
    ],
  },
  {
    title: 'Market',
    items: [
      { text: 'System Prices', icon: <PricesIcon />, path: '/market/prices' },
      { text: 'Balancing Insights', icon: <CompareIcon />, path: '/market/balancing' },
      { text: 'Data Upload', icon: <UploadIcon />, path: '/data/upload' },
    ],
  },
  {
    title: 'Balancing',
    items: [
      { text: 'Portfolio Monitor', icon: <PortfolioIcon />, path: '/balancing/portfolio' },
      { text: 'Bid-Offer Data', icon: <TrendingIcon />, path: '/balancing/bod' },
      { text: 'Acceptances (BOA)', icon: <AssessmentIcon />, path: '/balancing/boa' },
    ],
  },
  {
    title: 'Auction',
    items: [
      { text: 'Bid Strategies', icon: <AuctionIcon />, path: '/auction/strategies' },
      { text: 'Auction Analytics', icon: <AuctionAnalyticsIcon />, path: '/auction/analytics' },
      { text: 'Cross-Border', icon: <CrossBorderIcon />, path: '/auction/crossborder' },
    ],
  },
  {
    title: 'Intraday',
    items: [
      { text: 'Positions', icon: <IntradayIcon />, path: '/intraday/positions' },
      { text: 'Intraday Analytics', icon: <IntradayAnalyticsIcon />, path: '/intraday/analytics' },
    ],
  },
  {
    title: 'Analytics',
    items: [
      { text: 'Executive Summary', icon: <SummaryIcon />, path: '/analytics/summary' },
      { text: 'Portfolio Trader', icon: <PortfolioIcon />, path: '/analytics/trader' },
      { text: 'Plant Performance', icon: <PerformanceIcon />, path: '/analytics/performance' },
      { text: 'Missed Opportunities', icon: <MissedIcon />, path: '/analytics/missed' },
      { text: 'Historical Trends', icon: <TrendingIcon />, path: '/analytics/trends' },
      { text: 'Methodology', icon: <MethodologyIcon />, path: '/analytics/methodology' },
    ],
  },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useApp();

  // Whether to surface Market Data view at all (settings-controlled).
  const [showMarketData, setShowMarketData] = React.useState(() => readShowMarketData());
  // Whether to surface admin/observability/operations sections in the
  // sidebar. Default OFF — traders see only Trading.
  const [showAdmin, setShowAdmin] = React.useState(() => readShowAdmin());

  React.useEffect(() => {
    const handler = () => {
      setShowMarketData(readShowMarketData());
      setShowAdmin(readShowAdmin());
    };
    window.addEventListener('ecos:settings-changed', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('ecos:settings-changed', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  // View toggle — picks Platform vs Legacy menu. Persists in localStorage so
  // the user's choice sticks across reloads. Auto-detect from current path
  // on first mount so refreshing on /generation/fuel doesn't bounce them out.
  const [view, setView] = React.useState(() => {
    try {
      const stored = localStorage.getItem(VIEW_STORAGE_KEY);
      if (stored === VIEW_LEGACY || stored === VIEW_PLATFORM) return stored;
    } catch {}
    const path = window.location.pathname || '';
    if (path.startsWith('/platform') || path.startsWith('/observability')) return VIEW_PLATFORM;
    if (path.startsWith('/dashboard') || path.startsWith('/generation') || path.startsWith('/market') ||
        path.startsWith('/balancing') || path.startsWith('/auction') || path.startsWith('/intraday') ||
        path.startsWith('/analytics') || path.startsWith('/system') || path.startsWith('/data')) return VIEW_LEGACY;
    return VIEW_PLATFORM;
  });

  const handleViewChange = (_e, next) => {
    if (!next || next === view) return;
    setView(next);
    try { localStorage.setItem(VIEW_STORAGE_KEY, next); } catch {}
    // Drop the user on the canonical landing page for the chosen view.
    navigate(next === VIEW_PLATFORM ? '/platform' : '/dashboard');
  };

  // If Market Data is hidden, force platform view + only show platform menu.
  const effectiveView = showMarketData ? view : VIEW_PLATFORM;
  const rawMenuItems = effectiveView === VIEW_PLATFORM ? platformMenu : legacyMenu;
  // Filter admin sections in/out depending on showAdmin toggle.
  const menuItems = rawMenuItems.filter((s) => s.audience !== 'admin' || showAdmin);

  const [openSections, setOpenSections] = React.useState(() => {
    const initial = {};
    menuItems.forEach((section) => {
      initial[section.title] = section.defaultOpen !== false;
    });
    return initial;
  });

  // Re-seed openSections when the user toggles view OR admin so the new
  // menu's sections honour their defaultOpen flags.
  React.useEffect(() => {
    setOpenSections(() => {
      const next = {};
      menuItems.forEach((s) => { next[s.title] = s.defaultOpen !== false; });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, showAdmin]);

  const handleSectionToggle = (title) => {
    setOpenSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const isActive = (path) => location.pathname === path;

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={state.sidebarOpen}
      sx={{
        width: state.sidebarOpen ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          background: 'linear-gradient(180deg, rgba(26, 26, 46, 0.98) 0%, rgba(15, 15, 35, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          color: 'white',
          borderRight: '1px solid rgba(0, 212, 255, 0.1)',
          boxShadow: '4px 0 30px rgba(0, 0, 0, 0.3)',
          top: 64,
          height: 'calc(100% - 64px)',
        },
      }}
    >
      {/* Logo / Brand */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <ElectricIcon sx={{ color: '#00d4ff', fontSize: 32, mr: 1.5 }} />
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(90deg, #00d4ff, #00ff88)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: 1.2,
            }}
          >
            EnergyCosmos
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {view === VIEW_PLATFORM ? 'Algo Platform' : 'European Power Markets'}
          </Typography>
        </Box>
      </Box>

      {/* View toggle — only shown when Market Data view is enabled in settings */}
      {showMarketData && (
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={handleViewChange}
            size="small"
            fullWidth
            sx={{
              width: '100%',
              '& .MuiToggleButton-root': {
                flex: 1,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.5)',
                borderColor: 'rgba(255,255,255,0.1)',
                py: 0.7,
              },
              '& .Mui-selected': {
                backgroundColor: 'rgba(0,212,255,0.15) !important',
                color: '#00d4ff !important',
                borderColor: 'rgba(0,212,255,0.4) !important',
              },
            }}
          >
            <ToggleButton value={VIEW_PLATFORM}>Platform</ToggleButton>
            <ToggleButton value={VIEW_LEGACY}>Market Data</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Active Scope Indicator */}
      {state.scope.type !== ScopeTypes.SYSTEM && (
        <Box
          sx={{
            mx: 2,
            my: 1.5,
            p: 1.5,
            borderRadius: 2,
            backgroundColor: state.scope.type === ScopeTypes.PORTFOLIO
              ? 'rgba(0, 255, 136, 0.1)'
              : 'rgba(241, 196, 15, 0.1)',
            border: `1px solid ${state.scope.type === ScopeTypes.PORTFOLIO ? 'rgba(0, 255, 136, 0.2)' : 'rgba(241, 196, 15, 0.2)'}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderIcon sx={{ fontSize: 16, color: state.scope.type === ScopeTypes.PORTFOLIO ? '#00ff88' : '#f1c40f' }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Viewing
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: state.scope.type === ScopeTypes.PORTFOLIO ? '#00ff88' : '#f1c40f',
              fontWeight: 600,
              mt: 0.5,
            }}
          >
            {state.scope.portfolioName || state.scope.plantName || 'Custom Scope'}
          </Typography>
        </Box>
      )}

      {/* Navigation Menu */}
      <Box sx={{ overflow: 'auto', flex: 1, py: 1 }}>
        {menuItems.map((section) => (
          <Box key={section.title}>
            <ListItemButton
              onClick={() => handleSectionToggle(section.title)}
              sx={{
                py: 1,
                px: 2,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
              }}
            >
              <Typography
                variant="overline"
                sx={{ color: 'rgba(255,255,255,0.5)', flex: 1, fontSize: '0.7rem' }}
              >
                {section.title}
              </Typography>
              {openSections[section.title] ? (
                <ExpandLess sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 18 }} />
              ) : (
                <ExpandMore sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 18 }} />
              )}
            </ListItemButton>

            <Collapse in={openSections[section.title]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {section.items.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      onClick={() => navigate(item.path)}
                      sx={{
                        py: 1,
                        px: 3,
                        position: 'relative',
                        backgroundColor: isActive(item.path)
                          ? 'rgba(0, 212, 255, 0.12)'
                          : 'transparent',
                        borderLeft: isActive(item.path)
                          ? '3px solid #00d4ff'
                          : '3px solid transparent',
                        transition: 'all 0.2s ease',
                        '&::after': isActive(item.path) ? {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 4,
                          height: '60%',
                          background: 'linear-gradient(180deg, #00d4ff, #00ff88)',
                          borderRadius: '4px 0 0 4px',
                        } : {},
                        '&:hover': {
                          backgroundColor: isActive(item.path)
                            ? 'rgba(0, 212, 255, 0.18)'
                            : 'rgba(255,255,255,0.06)',
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: isActive(item.path) ? '#00d4ff' : 'rgba(255,255,255,0.6)',
                          minWidth: 36,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          color: isActive(item.path) ? '#00d4ff' : 'rgba(255,255,255,0.9)',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Box>
        ))}
      </Box>

      {/* Settings */}
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => navigate('/platform/settings')}
            sx={{
              py: 1.5,
              px: 3,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(255,255,255,0.6)', minWidth: 36 }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Settings"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                color: 'rgba(255,255,255,0.9)',
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
}
